// PRPForFaceData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const prpForFace: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "PRP Facial Treatment in Dubai | Natural Skin Rejuvenation at Elite Body Home",
            "description": "Discover PRP Facial Treatment in Dubai at Elite Body Home. Rejuvenate your skin naturally with platelet-rich plasma to reduce fine lines, improve texture, and restore a youthful glow.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/prp-facial-treatment-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "PRP Facial Treatment in Dubai | Natural Skin Rejuvenation at Elite Body Home",
                "description": "Discover PRP Facial Treatment in Dubai at Elite Body Home. Rejuvenate your skin naturally with platelet-rich plasma to reduce fine lines, improve texture, and restore a youthful glow.",
                "url": "https://elitebodyhome.com/services/prp-facial-treatment-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T09:18:44+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-37.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-37.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "PRP Facial Treatment Dubai",
                "image_type": "image/jpeg"
            },
        },
        "breadcrumbs": ["Home", "Services", "PRP Facial Treatment Dubai"],
        "hero": {
            "title": "PRP Facial Treatment Dubai",
            "subtitle": "Unlock Your Skin's Natural Glow with PRP",
            "description": "Platelet-Rich Plasma (PRP) therapy is an innovative aesthetic approach that harnesses the body's natural healing processes to improve skin texture and wrinkles and give an overall glow. This non-operative, minimally invasive technique has become highly popular for facial rejuvenation, leading to a youthful glow, without using any harsh chemicals or invasive surgical procedures."
        },
        "howItWorks": {
            "title": "What is PRP Facial Treatment?",
            "description": "PRP (platelet pin-rich plasma) facial treatment, popularly known as the 'vampire facial,' involves drawing a small amount of your blood, preparing it to extract as many platelets as possible by separating them from the red blood cells, and injecting or applying the plasma to the skin directly. Platelets, which are abundant in growth factors, promote the synthesis of collagen and cell renewal, resulting in skin that is smoother, firmer, and more youthful in appearance."
        },
        "procedureSteps": {
            "heading": "Important Note: PRP Facial Treatment Process",
            "para": "",
            "steps": [
                {
                    "title": "Natural skin rejuvenation",
                    "desc": "Heals with and improves the texture of skin with own blood",
                    "icon": "fa-solid fa-leaf"
                },
                {
                    "title": "Non-surgical",
                    "desc": "A less-invasive, non-surgical version of facelifts and fillers.",
                    "icon": "fa-solid fa-hand"
                },
                {
                    "title": "Boosts collagen production",
                    "desc": "Aids in reducing fine lines, wrinkles, and aging.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "Quick recovery time",
                    "desc": "Little recovery time compared to other oral and facial cosmetic procedures.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "benefits": {
            "heading": "Advantages of PRP Treatment for Face",
            "subheading": "",
            "para": "PRP facial treatments provide a range of advantages for individuals looking to naturally enhance the appearance of their skin. By harnessing the body's own healing capabilities, PRP provides a long-lasting, youthful glow, with minimal risk and side effects.",
            "items": [
                {
                    "title": "Improves Skin Texture & Tone",
                    "desc": "Targets uneven skin tone, fine lines and dullness.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Reduces Wrinkles",
                    "desc": "Stimulates collagen production — smoothing out & helping reduce fine lines & deeper wrinkles.",
                    "icon": "fa-solid fa-minus"
                },
                {
                    "title": "Minimizes Acne Scars",
                    "desc": "Assists in mending damaged skin and diminishing pitted scars.",
                    "icon": "fa-solid fa-circle"
                },
                {
                    "title": "Boosts Hydration",
                    "desc": "PRP improves moisture retention that gives skin more plump and healthy look.",
                    "icon": "fa-solid fa-tint"
                },
                {
                    "title": "Enhances Skin Elasticity",
                    "desc": "Its tightening, firming effect strengthens the skin structure for a firmer, tighter skin.",
                    "icon": "fa-solid fa-expand"
                },
                {
                    "title": "Safe & Natural",
                    "desc": "As it incorporates your own plasma, it carries little risk of allergic reactions or complications.",
                    "icon": "fa-solid fa-shield-alt"
                }
            ]
        },
        "eligibility": {
            "heading": "PRP Facial Treatment: Who is Right for It?",
            "subheading": "",
            "para": "The ideal candidate for PRP treatment of the face would be someone who desires a natural, non-surgical solution to rejuvenate their skin. But, as with any cosmetic procedure, eligibility is based on certain criteria.",
            "items": [
                {
                    "title": "Adult Applications for Facial Rejuvenation",
                    "desc": "Well-suited for mild to moderate skin aging concerns.",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "Non-Smokers",
                    "desc": "Smoking can inhibit healing and diminish the benefits of the treatment.",
                    "icon": "fa-solid fa-smoking-ban"
                },
                {
                    "title": "People with Good Overall Health",
                    "desc": "Those who do not have serious health issues, including blood disorders or infections.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Not Pregnant or Breastfeeding",
                    "desc": "PRP treatment is typically not advised in the duration of pregnancy or breastfeeding.",
                    "icon": "fa-solid fa-baby"
                }
            ]
        },
        "preparationTips": {
            "heading": "Before PRP Facial Treatment: How to Prepare",
            "subheading": "",
            "para": "PRP facial treatments produce the best results only if you've taken care beforehand. Adhering to certain protocols prior to your appointment will help maximize the procedure's results and reduce its risks.",
            "items": [
                {
                    "title": "Do not take blood-thinning medications",
                    "desc": "Do not take aspirin or anti-inflammatory drugs for several days before, unless your doctor instructs you to.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Stay Hydrated",
                    "desc": "Make sure to hydrate well up to your treatment.",
                    "icon": "fa-solid fa-tint"
                },
                {
                    "title": "Avoid Sun Exposure",
                    "desc": "Minimize sun exposure and skip tanning beds one week prior to your session.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Stop Using Skincare Products",
                    "desc": "Refrain from using retinoids, acids or exfoliating products a few days before the treatment.",
                    "icon": "fa-solid fa-stop"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "The recovery period after PRP facial treatments is usually rapid with little downtime. Patients can typically go back to their daily routines right away, but some aftercare is needed for best results.",
            "items": [
                {
                    "title": "Immediate Effects",
                    "desc": "Minimal inflammation, erythema and tautness of the-treated area resolves in 1-3 days.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Gradual Results",
                    "desc": "Visible skin improvements occur over a period of weeks as collagen production is stimulated.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Long-lasting Rejuvenation",
                    "desc": "Results can be long-lasting – up to 12 months – depending on individual skin type, lifestyle and skin care routine.",
                    "icon": "fa-solid fa-calendar"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "Is PRP treatment painful?",
                    "answer": "PRP treatment involves minimal discomfort. Topical numbing cream is often applied to reduce any sensation during the procedure."
                },
                {
                    "question": "How long does it take to see results from PRP facial treatment?",
                    "answer": "Results typically become visible after 2-3 weeks, with continued improvement over several months as collagen production increases."
                },
                {
                    "question": "How many sessions are needed?",
                    "answer": "For optimal results, 3-4 sessions spaced a month apart are recommended. Maintenance treatments can be done once or twice a year."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "PRP treatment is generally safe, with minimal side effects such as mild swelling, redness, or bruising, which subside within a few days."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-37.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/PRP-Facial-Treatment-Dubaid-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/PRP-Facial-Treatment-Dubaid-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/PRP-Facial-Treatment-Dubaid-3.jpg"
            ]
        },
        "product": {
            "id": 116,
            "title": "PRP Facial Treatment",
            "price": 390,
            "description": "PRP Facial Treatment in Dubai uses your body's own platelet-rich plasma to naturally boost collagen, improve skin tone, and reduce signs of aging. Ideal for dull or aging skin with minimal downtime."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "علاج الوجه بالبلازما الغنية بالصفائح الدموية (PRP) في دبي | ايليت بودي هوم",
            "description": "اكتشف علاج الوجه بالبلازما الغنية بالصفائح الدموية (PRP) في دبي عند ايليت بودي هوم. جدد بشرتك بشكل طبيعي باستخدام البلازما الغنية بالصفائح الدموية لتقليل الخطوط الدقيقة، تحسين الملمس، واستعادة الإشراقة الشبابية.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/prp-facial-treatment-dubai/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "علاج الوجه بالبلازما الغنية بالصفائح الدموية (PRP) في دبي | ايليت بودي هوم",
                "description": "اكتشف علاج الوجه بالبلازما الغنية بالصفائح الدموية (PRP) في دبي عند ايليت بودي هوم. جدد بشرتك بشكل طبيعي باستخدام البلازما الغنية بالصفائح الدموية لتقليل الخطوط الدقيقة، تحسين الملمس، واستعادة الإشراقة الشبابية.",
                "url": "https://elitebodyhome.com/services/prp-facial-treatment-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T09:18:44+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-37.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-37.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "علاج الوجه بالبلازما الغنية بالصفائح الدموية دبي",
                "image_type": "image/jpeg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج الوجه بالبلازما دبي"],
        "hero": {
            "title": "علاج الوجه بالبلازما الغنية بالصفائح الدموية (PRP) في دبي",
            "subtitle": "اكتشف إشراقة بشرتك الطبيعية مع علاج البلازما (PRP)",
            "description": "يعد علاج البلازما الغنية بالصفائح الدموية أسلوب تجميلي مبتكر يعتمد على تحفيز عمليات الشفاء الطبيعية في الجسم لتحسين ملمس البشرة، والتجاعيد، ومنح الوجه إشراقة صحية. يُعتبر هذا الإجراء غير الجراحي والبسيط من أكثر الخيارات شيوعًا لتجديد الوجه، حيث يمنحك مظهرًا أكثر شبابًا دون الحاجة إلى مواد كيميائية قاسية أو إجراءات جراحية."
        },
        "howItWorks": {
            "title": "ما هو علاج الوجه بالبلازما الغنية بالصفائح الدموية؟",
            "description": "يعرف علاج الوجه بالبلازما الغنية بالصفائح الدموية باسم فيشل مصاص الدماء أيضًا، وهو إجراء تجميلي يتم فيه سحب كمية صغيرة من دمك، ثم معالجتها لفصل الصفائح الدموية عن خلايا الدم الحمراء، وبعد ذلك تُحقن البلازما أو تُوضع مباشرةً على البشرة. تحتوي الصفائح الدموية على تركيز عالٍ من عوامل النمو والتي تُحفز إنتاج الكولاجين وتجديد الخلايا، مما يؤدي إلى مظهر بشرة أكثر نعومة، وتماسكًا، وشبابًا."
        },
        "procedureSteps": {
            "heading": "ملاحظة هامة: علاج الوجه بالبلازما الغنية بالصفائح الدموية (PRP)",
            "para": "",
            "steps": [
                {
                    "title": "تجديد طبيعي للبشرة",
                    "desc": "يعتمد الإجراء على دمك لتحفيز عملية الشفاء الطبيعية وتحسين ملمس البشرة.",
                    "icon": "fa-solid fa-leaf"
                },
                {
                    "title": "إجراء غير جراحي",
                    "desc": "يُعد خيارًا تجميليًا أقل تدخلاً من عمليات شد الوجه أو الفيلر.",
                    "icon": "fa-solid fa-hand"
                },
                {
                    "title": "يعزز إنتاج الكولاجين",
                    "desc": "يساعد في التقليل من الخطوط الدقيقة، والتجاعيد، وعلامات التقدم في العمر.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "فترة تعافي سريعة",
                    "desc": "يتميز بوقت تعافٍ قصير مقارنةً بالإجراءات التجميلية الأخرى للوجه أو الأدوية الفموية.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد علاج الوجه بالبلازما الغنية بالصفائح الدموية",
            "subheading": "",
            "para": "يوفر علاج الوجه بالبلازما الغنية بالصفائح الدموية مجموعة من الفوائد للأشخاص الراغبين في تحسين مظهر بشرتهم بشكل طبيعي، وذلك من خلال تحفيز قدرات الجسم الطبيعية على التجديد، مما يمنح البشرة إشراقة شبابية تدوم طويلاً مع أدنى حد من المخاطر أو الآثار الجانبية.",
            "items": [
                {
                    "title": "تحسين ملمس البشرة وتوحيد لونها",
                    "desc": "يساعد على استهداف تفاوت اللون والخطوط الدقيقة وبهتان البشرة.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "تقليل التجاعيد",
                    "desc": "يحفز إنتاج الكولاجين، مما يساهم في تنعيم البشرة وتقليل مظهر التجاعيد السطحية والعميقة.",
                    "icon": "fa-solid fa-minus"
                },
                {
                    "title": "تخفيف ندبات حب الشباب",
                    "desc": "يساعد على إصلاح خلايا البشرة التالفة والتقليل من الندبات المحفورة.",
                    "icon": "fa-solid fa-circle"
                },
                {
                    "title": "تعزيز ترطيب البشرة",
                    "desc": "يساعد في تحسين احتفاظ البشرة بالترطيب، مما يمنحها مظهرًا أكثر امتلاًء وصحة.",
                    "icon": "fa-solid fa-tint"
                },
                {
                    "title": "زيادة مرونة الجلد",
                    "desc": "يؤثر على شد البشرة وتماسكها، ويُعزز بنية الجلد، مما يمنحك بشرة أكثر قوة وثباتًا.",
                    "icon": "fa-solid fa-expand"
                },
                {
                    "title": "إجراء آمن وطبيعي",
                    "desc": "يُعد آمنًا بدرجة كبيرة مع احتمال ضئيل جدًا لحدوث ردود فعل تحسسية أو مضاعفات لاعتماده على بلازما دمك الخاصة.",
                    "icon": "fa-solid fa-shield-alt"
                }
            ]
        },
        "eligibility": {
            "heading": "علاج الوجه بالبلازما الغنية بالصفائح الدموية: لمن يُناسب؟",
            "subheading": "",
            "para": "يكون المرشح المثالي لعلاج الوجه بالبلازما هو الشخص الذي يبحث عن حل طبيعي وغير جراحي لتجديد البشرة. ومع ذلك، تعتمد الملائمة مع هذا النوع من الإجراءات على معايير معينة.",
            "items": [
                {
                    "title": "البالغون الراغبون في تجديد شباب البشرة",
                    "desc": "يلائم الأشخاص الذين يعانون من علامات شيخوخة البشرة الخفيفة أو المتوسطة.",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "الأشخاص غير المدخنين",
                    "desc": "يمكن أن يؤثر التدخين سلبًا على عملية التعافي ويقلل من فعالية العلاج.",
                    "icon": "fa-solid fa-smoking-ban"
                },
                {
                    "title": "الأشخاص الذين يتمتعون بصحة عامة جيدة",
                    "desc": "يُفضل ألا يكون لدى المريض مشكلات صحية مثل أمراض الدم أو التهابات.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "لا يناسب الحامل والمُرضعة",
                    "desc": "لا يُنصح بإجراء هذا العلاج خلال فترات الحمل أو الرضاعة.",
                    "icon": "fa-solid fa-baby"
                }
            ]
        },
        "preparationTips": {
            "heading": "قبل إجراء علاج الوجه بالبلازما الغنية بالصفائح الدموية: كيف تستعد؟",
            "subheading": "",
            "para": "تمنح جلسات بلازما الوجه أفضل النتائج فقط عند الالتزام بالتحضيرات اللازمة مسبقًا، حيث يساعد الالتزام ببعض التعليمات قبل موعد الجلسة على تعزيز نتائج الإجراء وتقليل احتمالية أي مضاعفات.",
            "items": [
                {
                    "title": "تجنب الأدوية المُميعة للدم",
                    "desc": "توقف عن تناول الأسبرين أو مضادات الالتهاب قبل عدة أيام من الجلسة، إلا إذا أوصى الطبيب بخلاف ذلك.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "حافظ على ترطيب جسمك",
                    "desc": "اشرب كميات كافية من الماء حتى موعد علاجك.",
                    "icon": "fa-solid fa-tint"
                },
                {
                    "title": "تجنب التعرض لأشعة الشمس",
                    "desc": "قلل من التعرض لأشعة الشمس المباشرة وتجنب استخدام أجهزة التسمير لمدة أسبوع قبل العلاج.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "توقف عن استخدام منتجات العناية بالبشرة",
                    "desc": "امتنع عن استخدام المنتجات التي تحتوي على الريتينويد، أو الأحماض، أو المقشرات لعدة أيام قبل الجلسة.",
                    "icon": "fa-solid fa-stop"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "تكون فترة التعافي بعد جلسات بلازما الوجه سريعة عادةً، ولا تتطلب وقتًا طويلًا للتوقف عن الأنشطة اليومية. يمكن للمرضى العودة إلى روتينهم المعتاد فورًا، مع الالتزام بإرشادات العناية لما بعد الجلسة لتحقيق أفضل النتائج.",
            "items": [
                {
                    "title": "تأثير فوري",
                    "desc": "قد تظهر بعض الالتهابات، أو احمرار الجلد، أو الشعور بشدّ الجلد في المنطقة المعالجة، وتزول هذه الآثار خلال يوم إلى ثلاثة أيام.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "نتائج تدريجية",
                    "desc": "يبدأ تحسن البشرة في الظهور خلال الأسابيع التالية للعلاج، مع تحفيز إنتاج الكولاجين.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "نتائج تدوم طويلًا",
                    "desc": "تدوم النتائج لفترة تصل إلى 12 شهرًا، وذلك حسب نوع البشرة، ونمط الحياة، وروتين العناية اليومي بالبشرة.",
                    "icon": "fa-solid fa-calendar"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "هل علاج البلازما مؤلم؟",
                    "answer": "يتضمن علاج البلازما حد أدنى من الانزعاج. عادةً ما يتم تطبيق كريم مخدر موضعي لتقليل أي إحساس أثناء الإجراء."
                },
                {
                    "question": "كم من الوقت يستغرق ظهور نتائج علاج الوجه بالبلازما؟",
                    "answer": "تبدأ النتائج في الظهور عادةً بعد 2-3 أسابيع، مع استمرار التحسن على مدار عدة أشهر مع زيادة إنتاج الكولاجين."
                },
                {
                    "question": "كم عدد الجلسات المطلوبة؟",
                    "answer": "للحصول على أفضل النتائج، يُنصح بـ 3-4 جلسات بفاصل شهر بينها. يمكن إجراء جلسات صيانة مرة أو مرتين سنويًا."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "علاج البلازما آمن بشكل عام، مع آثار جانبية بسيطة مثل تورم خفيف، احمرار، أو كدمات، والتي تختفي خلال بضعة أيام."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
};
